import { getProfile } from '@/api/user';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { IFetchUserInfo, transformUserInfo } from '@/shared/types/userInfo';
import { useAuthStore } from '@/store';
import { RouteRecordRaw, createRouter, createWebHistory } from 'vue-router';
import { toast } from 'vue3-toastify';
import permissionMapping, { mappingRolePermissions } from './permissionMapping';
import { transformPermission } from '@/shared/types/permission';
import { setupTabGuard } from './tabGuard';

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'root',
    redirect: '/home',
    component: () =>
      import(/* webpackChunkName: "layout" */ '@/layout/DefaultLayout.vue'),
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/views/dashboard/DashboardView.vue'),
        meta: {
          title: '儀表板',
          icon: 'mdi-view-dashboard',
          addToTabs: true
        },
        children: [
          {
            path: 'dashboard-list',
            name: 'DashboardList',
            component: () => import('@/views/dashboard/DashboardList.vue'),
            meta: {
              title: '儀表板列表',
              addToTabs: true
            }
          },
          {
            path: 'dashboard-design',
            name: 'DashboardDesign',
            component: () =>
              import('@/views/customDashboard/EditDashboard.vue'),
            meta: {
              title: '儀表板設計',
              addToTabs: true
            }
          },
          {
            path: 'dashboard-preview',
            name: 'DashboardPreview',
            component: () =>
              import('@/views/customDashboard/PreviewDashboard.vue'),
            meta: {
              title: '儀表板預覽',
              addToTabs: true
            }
          },
          {
            path: '',
            name: 'DashboardIndex',
            redirect: 'dashboard-list'
          }
        ]
      },
      {
        path: 'home',
        name: 'Home',
        component: () => import('@/views/home/HomeView.vue'),
        meta: {
          title: '首頁',
          icon: 'mdi-home',
          addToTabs: true
        }
      },
      {
        path: 'data-platform',
        name: 'DataPlatform',
        component: () => import('@/views/dataPlatform/DataPlatformView.vue'),
        meta: {
          title: '數據平台',
          icon: 'mdi-database',
          addToTabs: true
        },
        children: [
          {
            path: 'dashboard',
            name: 'DataDashboard',
            component: () =>
              import('@/views/dataPlatform/DataDashboardView.vue'),
            meta: { title: '數據總覽', addToTabs: true }
          },
          {
            path: 'reports',
            name: 'DataReports',
            component: () => import('@/views/dataPlatform/DataReportsView.vue'),
            meta: { title: '統計報表', addToTabs: true }
          },
          {
            path: 'inspection-report',
            name: 'InspectionReport',
            component: () =>
              import(
                '@/views/dataPlatform/InspectionReport/InspectionReportView.vue'
              ),
            meta: { title: '檢查報告', addToTabs: true }
          },
          {
            path: 'source',
            name: 'DataSourceManagement',
            component: () =>
              import('@/views/dataPlatform/DataSourceManagementView.vue'),
            meta: { title: '資料源管理', addToTabs: true }
          },
          {
            path: 'tasks',
            name: 'DataTasks',
            component: () => import('@/views/dataPlatform/DataTasksView.vue'),
            meta: { title: '數據任務', addToTabs: true }
          },
          {
            path: '',
            name: 'DataPlatformIndex',
            redirect: 'dashboard'
          }
        ]
      },
      {
        path: 'custom-dashboard',
        name: 'CustomDashboard',
        redirect: '/custom-dashboard/preview-dashboard',
        children: [
          {
            path: 'edit-dashboard',
            name: 'EditDashboard',
            component: () => import('@/views/customDashboard/EditDashboard.vue')
          },
          {
            path: 'preview-dashboard',
            name: 'PreviewDashboard',
            component: () =>
              import('@/views/customDashboard/PreviewDashboard.vue')
          }
        ]
      },
      {
        path: 'rule-engine',
        name: 'RuleEngine',
        component: () => import('@/views/flow/FlowManagerView.vue'),
        meta: {
          title: '流程管理',
          icon: 'mdi-repeat',
          addToTabs: true
        }
      },
      {
        path: 'alarm',
        name: 'Alarm',
        meta: {
          title: '警報管理',
          icon: 'mdi-alert',
          addToTabs: true
        },
        children: [
          {
            path: 'unprocessed-alert',
            name: 'UnprocessedAlertEvent',
            component: () => import('@/views/alarm/AlarmView.vue')
          },
          {
            path: 'processing-alert',
            name: 'ProcessingAlertEvent',
            component: () => import('@/views/alarm/AlarmView.vue')
          },
          {
            path: 'processed-alert',
            name: 'ProcessedAlertEvent',
            component: () => import('@/views/alarm/AlarmView.vue')
          }
        ]
      },
      {
        path: 'cctv',
        name: 'CCTV',
        component: () => import('@/views/cctv/CCTVView.vue'),
        meta: {
          title: 'CCTV',
          icon: 'mdi-cctv',
          addToTabs: true
        }
      },

      {
        path: 'profile',
        name: 'Profile',
        component: () => import('@/views/profile/ProfileView.vue')
      },
      {
        path: 'theme-demo',
        name: 'ThemeDemo',
        component: () => import('@/views/ThemeDemo.vue'),
        meta: {
          title: '主題演示',
          icon: 'mdi-palette',
          addToTabs: true
        }
      },
      {
        path: 'terms',
        name: 'TermsOfService',
        component: () => import('@/views/legal/TermsOfServiceView.vue')
      },
      {
        path: 'privacy',
        name: 'PrivacyPolicy',
        component: () => import('@/views/legal/PrivacyPolicyView.vue')
      },
      {
        path: 'device-management',
        name: 'DeviceManagement',
        meta: {
          title: '設備管理',
          icon: 'mdi-devices',
          addToTabs: true
        },
        component: () =>
          import('@/views/deviceManagement/DeviceManagementView.vue'),
        children: [
          {
            path: 'device-list',
            name: 'DeviceList',
            component: () =>
              import('@/views/deviceManagement/DeviceList/DeviceListView.vue'),
            meta: {
              title: '設備列表',
              addToTabs: true
            }
          },
          {
            path: 'sub-device-management',
            name: 'SubDeviceManagement',
            component: () =>
              import(
                '@/views/deviceManagement/SubDeviceManagement/SubDeviceManagementView.vue'
              ),
            meta: {
              title: '子設備管理',
              addToTabs: true
            }
          },
          {
            path: 'command-management',
            name: 'CommandManagement',
            component: () =>
              import(
                '@/views/deviceManagement/CommandManagement/CommandManagementView.vue'
              ),
            meta: {
              title: '指令管理',
              addToTabs: true
            }
          },
          {
            path: '',
            name: 'DeviceManagementIndex',
            redirect: 'device-list'
          }
        ]
      },
      {
        path: 'alarm-management',
        name: 'AlarmManagement',
        component: () =>
          import('@/views/alarmManagement/AlarmManagementView.vue'),
        children: [
          {
            path: 'rules',
            name: 'AlarmRules',
            component: () =>
              import('@/views/alarmManagement/AlarmRulesView.vue'),
            meta: { title: '告警規則', addToTabs: true }
          },
          {
            path: 'list',
            name: 'AlarmList',
            component: () =>
              import('@/views/alarmManagement/AlarmListView.vue'),
            meta: { title: '告警列表', addToTabs: true }
          },
          {
            path: 'templates',
            name: 'AlarmTemplates',
            component: () =>
              import('@/views/alarmManagement/AlarmTemplatesView.vue'),
            meta: { title: '告警模板', addToTabs: true }
          },
          {
            path: 'notifications',
            name: 'NotificationLists',
            component: () =>
              import('@/views/alarmManagement/NotificationListsView.vue'),
            meta: { title: '通知列表', addToTabs: true }
          },
          {
            path: '',
            name: 'AlarmManagementIndex',
            redirect: 'rules'
          }
        ]
      },
      {
        path: 'application-management',
        name: 'ApplicationManagement',
        component: () =>
          import(
            '@/views/microserviceManagement/MicroserviceManagementView.vue'
          ),
        meta: {
          title: '應用管理',
          icon: 'mdi-application',
          addToTabs: true
        },
        children: [
          {
            path: 'service-overview',
            name: 'ServiceOverview',
            component: () =>
              import(
                '@/views/microserviceManagement/ServiceOverview/ServiceOverviewView.vue'
              ),
            meta: {
              title: '應用總覽',
              addToTabs: true
            }
          },
          {
            path: 'fuxa-config',
            name: 'FuxaConfig',
            component: () =>
              import(
                '@/views/microserviceManagement/FuxaConfig/FuxaConfigView.vue'
              ),
            meta: {
              title: 'Fuxa 配置',
              addToTabs: true
            }
          },
          {
            path: 'node-red-config',
            name: 'NodeRedConfig',
            component: () =>
              import(
                '@/views/microserviceManagement/NodeRedConfig/NodeRedConfigView.vue'
              ),
            meta: {
              title: 'Node-RED 配置',
              addToTabs: true
            }
          },
          {
            path: '',
            name: 'ApplicationManagementIndex',
            redirect: 'service-overview'
          }
        ]
      },
      {
        path: 'monitoring',
        name: 'Monitoring',
        component: () =>
          import('@/views/deviceMonitoring/DeviceMonitoringView.vue'),
        meta: {
          title: '監控管理',
          icon: 'mdi-monitor-dashboard',
          addToTabs: true
        },
        children: [
          {
            path: 'device-status',
            name: 'DeviceStatusMonitor',
            component: () =>
              import(
                '@/views/deviceMonitoring/DeviceStatusMonitor/DeviceStatusMonitorView.vue'
              ),
            meta: {
              title: '設備狀態',
              addToTabs: true
            }
          },
          {
            path: 'real-time-metrics',
            name: 'RealTimeMetrics',
            component: () =>
              import(
                '@/views/deviceMonitoring/RealTimeMetrics/RealTimeMetricsView.vue'
              ),
            meta: {
              title: '即時指標',
              addToTabs: true
            }
          },
          {
            path: 'monitor-properties',
            name: 'MonitorProperties',
            component: () =>
              import(
                '@/views/deviceMonitoring/MonitorProperties/MonitorPropertiesView.vue'
              ),
            meta: {
              title: '監控屬性',
              addToTabs: true
            }
          },
          {
            path: '',
            name: 'MonitoringIndex',
            redirect: 'device-status'
          }
        ]
      },
      {
        path: 'notification-management',
        name: 'NotificationManagement',
        redirect: '/notification-management/notification-center',
        children: [
          {
            path: 'notification-center',
            name: 'NotificationCenter',
            component: () =>
              import(
                '@/views/notificationManagement/NotificationCenter/NotificationCenterView.vue'
              ),
            meta: {
              title: '通知中心',
              addToTabs: true
            }
          },
          {
            path: 'notification-settings',
            name: 'NotificationSettings',
            component: () =>
              import(
                '@/views/notificationManagement/NotificationSettings/NotificationSettingsView.vue'
              ),
            meta: {
              title: '通知設定',
              addToTabs: true
            }
          },
          {
            path: 'notification-history',
            name: 'MessageHistory',
            component: () =>
              import(
                '@/views/notificationManagement/MessageHistory/MessageHistoryView.vue'
              ),
            meta: {
              title: '通知歷史',
              addToTabs: true
            }
          }
        ]
      },

      {
        path: 'flow',
        name: 'Flow',
        component: () => import('@/views/workflow/WorkflowManagementView.vue'),
        meta: {
          title: '流程管理',
          icon: 'mdi-repeat',
          addToTabs: true
        }
      },
      {
        path: 'system-management',
        name: 'SystemManagement',
        component: () => import('@/views/system/SystemManagementView.vue'),
        meta: {
          title: '系統管理',
          icon: 'mdi-cog-outline',
          addToTabs: false
        },
        children: [
          {
            path: 'global-management',
            name: 'GlobalManagement',
            component: () => import('@/views/system/GlobalManagement.vue'),
            meta: {
              title: '全域管理',
              icon: 'mdi-earth',
              addToTabs: true
            }
          },
          {
            path: 'organization-overview',
            name: 'OrganizationOverview',
            component: () =>
              import(
                '@/views/permissionManagement/SystemSettings/SystemSettingsView.vue'
              ),
            meta: {
              title: '組織總覽',
              icon: 'mdi-office-building',
              addToTabs: true
            }
          },
          {
            path: 'user-management',
            name: 'UserManagement',
            component: () => import('@/views/system/UserManagementNewView.vue'),
            redirect: '/system-management/user-management/account-management',
            meta: {
              title: '用戶管理',
              icon: 'mdi-account-group',
              addToTabs: true
            },
            children: [
              {
                path: 'account-management',
                name: 'AccountManagement',
                component: () =>
                  import('@/views/system/UserManagementNewView.vue'),
                meta: {
                  title: '帳號管理',
                  addToTabs: true
                }
              },
              {
                path: 'organization-management',
                name: 'OrganizationManagementTab',
                component: () =>
                  import('@/views/system/UserManagementNewView.vue'),
                meta: {
                  title: '組織管理',
                  addToTabs: true
                }
              },
              {
                path: 'role-permissions',
                name: 'RolePermissions',
                component: () =>
                  import('@/views/system/UserManagementNewView.vue'),
                meta: {
                  title: '權限管理',
                  addToTabs: true
                }
              }
            ]
          },
          {
            path: 'config-service',
            name: 'ConfigService',
            component: () => import('@/views/system/ConfigService.vue'),
            meta: {
              title: '配置服務',
              icon: 'mdi-cog-outline',
              addToTabs: true
            }
          },
          {
            path: 'monitoring-logs',
            name: 'MonitoringLogs',
            component: () => import('@/views/system/MonitoringLogs.vue'),
            meta: {
              title: '監控與日誌',
              icon: 'mdi-chart-line',
              addToTabs: true
            }
          }
        ]
      },
      {
        path: 'api-management',
        name: 'ApiManagement',
        component: () => import('@/views/apiManagement/ApiManagementView.vue'),
        children: [
          {
            path: 'interface',
            name: 'ApiInterface',
            component: () =>
              import('@/views/apiManagement/InterfaceManagement.vue'),
            meta: {
              title: '介面管理',
              addToTabs: true
            }
          },
          {
            path: 'model',
            name: 'ApiModel',
            component: () =>
              import('@/views/apiManagement/ModelManagement.vue'),
            meta: {
              title: '模型管理',
              addToTabs: true
            }
          },
          {
            path: '',
            name: 'ApiManagementIndex',
            redirect: 'interface'
          }
        ]
      }
    ]
  },
  {
    path: '/auth',
    name: 'Auth',
    redirect: '/auth/login',
    component: () =>
      import(/* webpackChunkName: "layout" */ '@/layout/EmptyLayout.vue'),
    children: [
      {
        path: 'login',
        name: 'Login',
        component: () => import('@/views/auth/login/LoginView.vue')
      },
      {
        path: 'resetPassword',
        name: 'ResetPassword',
        component: () => import('../views/auth/resetPassword/ResetPassword.vue')
      }
    ]
  },
  {
    path: '/i18n-test',
    name: 'I18nTestPublic',
    component: () => import('@/views/i18n-test/I18nTestSimpleView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/device-list',
    redirect: '/device-management/device-list'
  }
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
});

// 設置標籤頁守衛
setupTabGuard(router);

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();

  // 檢查是否為公開路由
  if (
    to.meta?.requiresAuth === false ||
    to.name === 'Login' ||
    to.name === 'I18nTestPublic'
  ) {
    return next();
  }

  // 檢查是否有 token
  if (!localStorage.getItem('token')) {
    return next({ name: 'Login' });
  }

  // 如果已登入但用戶資訊未載入
  if (
    localStorage.getItem('token') &&
    (authStore.userInfo.username === '尚未登入' ||
      authStore.userInfo.username === '')
  ) {
    try {
      const profile = await getProfile();

      if (!profile.data) {
        toast('會員資料抓取異常', {
          autoClose: 3000,
          position: 'top-right',
          type: 'error',
          closeButton: false,
          hideProgressBar: true
        });
        localStorage.removeItem('token');
        return next({ name: 'Login' });
      }

      const rolePermissions = mappingRolePermissions(
        permissionMapping,
        profile.data.permissions.map(transformPermission)
      );

      authStore.setLogin(true);
      authStore.setUserInfo(transformUserInfo(profile.data));
      authStore.setPermissions(rolePermissions);
      return next();
    } catch (error) {
      localStorage.removeItem('token');
      return next({ name: 'Login' });
    }
  }

  return next();
});

export default router;
